import React, { useEffect, useMemo, useState } from 'react';
import './styles.css';
import { Segmented, Spin, Tabs, Empty, Button, Tooltip } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { dashboardApi, contracts as contractsApi } from '../api';
import ChartComponent from '../dashboard/ChartComponent';
import { extractContract } from '../other/usefulFunctions';
import { userRole, employeeName } from '../../GetRole.utils';

dayjs.extend(customParseFormat);

const CONTRACT_TYPES = [
  { type: 'NDA', key: 'nondisclosureagreement(NDA)', path: 'nda' },
  { type: 'MSA', key: 'masterserviceagreement(MSA)', path: 'msa' },
  { type: 'SOW', key: 'statementofwork(SOW)', path: 'sow' },
  { type: 'PO', key: 'purchaseorder(PO)', path: 'po' },
];

// cStatusId values used by the contract list pages
const STATUS = { draft: 1, pending: 2, active: 3, inactive: 4 };

const SEGMENTS = [
  { key: 'active', label: 'Active', color: '#12B76A', status: STATUS.active },
  { key: 'pending', label: 'Pending', color: '#F79009', status: STATUS.pending },
  { key: 'draft', label: 'Draft', color: '#2E90FA', status: STATUS.draft },
  { key: 'inactive', label: 'Inactive', color: '#98A2B3', status: STATUS.inactive },
];

const PERIODS = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
];

const canApprove = userRole !== 'bdTeam';

const greeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const formatDate = (isoDateString) => dayjs(isoDateString).format('DD/MM/YYYY');

// y-axis ticks for the stacked column chart: small counts get every integer,
// bigger counts get rounded up to a multiple of 6
const yAxisFor = (maxCount) => {
  if (maxCount === 0) return [2, 1, 0];
  const top =
    maxCount < 6
      ? maxCount + 1
      : maxCount % 6 !== 0
      ? Math.ceil(maxCount / 6) * 6
      : maxCount + 1;
  const ticks = [];
  for (let i = top; i >= 0; i--) ticks.push(i);
  return ticks;
};

const daysUntil = (ddmmyyyy) => {
  const date = dayjs(ddmmyyyy, 'DD/MM/YYYY');
  return date.isValid() ? date.startOf('day').diff(dayjs().startOf('day'), 'day') : null;
};

const dueText = (days) => {
  if (days === null) return '';
  if (days <= 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState(30);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [pending, setPending] = useState({ rows: [], total: 0 });
  const [expiring, setExpiring] = useState([]);
  const [attentionLoading, setAttentionLoading] = useState(true);
  const [chartTab, setChartTab] = useState('expiring');
  const [chartDays, setChartDays] = useState(30);
  const [chart, setChart] = useState({ data: [], yAxis: [2, 1, 0] });
  const [chartLoading, setChartLoading] = useState(false);

  const goToContracts = (path, status, expiredDays) => {
    const params = new URLSearchParams({ status: [].concat(status).join(',') });
    if (expiredDays) params.set('expiredDays', expiredDays);
    navigate({ pathname: `/${path}`, search: `?${params.toString()}` });
  };

  const goToClients = (withoutContract) => {
    navigate({
      pathname: '/clients',
      search: `?withoutContract=${withoutContract}&entities=0,1`,
    });
  };

  // ---- summary numbers
  useEffect(() => {
    let cancelled = false;
    setSummaryLoading(true);
    dashboardApi
      .getContracts(period)
      .then((response) => {
        if (cancelled) return;
        const data = response.data;
        const byType = CONTRACT_TYPES.map((t) => {
          const counts = data.activeContract[t.key] || {};
          return {
            ...t,
            active: Number(counts.active) || 0,
            pending: Number(counts.pendingapproval) || 0,
            draft: Number(counts.draft) || 0,
            inactive: Number(counts.inactive) || 0,
            expiring: Number(data.expiredContract[t.key]) || 0,
          };
        });
        setSummary({ byType, clients: data.activeClient || {} });
      })
      .catch((error) => console.error('Error fetching dashboard data:', error))
      .finally(() => !cancelled && setSummaryLoading(false));
    return () => {
      cancelled = true;
    };
  }, [period]);

  // ---- "needs attention": pending approvals + contracts about to expire
  useEffect(() => {
    let cancelled = false;
    setAttentionLoading(true);
    const pendingRequest = canApprove
      ? contractsApi.pendingContracts(1)
      : Promise.resolve(null);
    const expiringRequests = CONTRACT_TYPES.map((t) =>
      contractsApi
        .contractsAllData(1, t.path, String(STATUS.active), '', '', '', '', period)
        .then((res) => res.data.data.map((row) => ({ ...row, type: t.type, path: t.path })))
        .catch(() => [])
    );

    Promise.all([pendingRequest, Promise.all(expiringRequests)])
      .then(([pendingRes, expiringByType]) => {
        if (cancelled) return;
        if (pendingRes) {
          setPending({
            rows: pendingRes.data.data.map((row) => ({
              ...row,
              type: extractContract(row.contractName),
            })),
            total: Number(pendingRes.data.totalContractCount) || 0,
          });
        }
        const rows = expiringByType
          .flat()
          .map((row) => ({ ...row, days: daysUntil(row.expiryDate) }))
          .sort((a, b) => (a.days ?? 9999) - (b.days ?? 9999));
        setExpiring(rows);
      })
      .catch((error) => console.error('Error fetching attention items:', error))
      .finally(() => !cancelled && setAttentionLoading(false));
    return () => {
      cancelled = true;
    };
  }, [period]);

  // ---- chart
  useEffect(() => {
    let cancelled = false;
    setChartLoading(true);
    const request =
      chartTab === 'expiring'
        ? dashboardApi.getGraphData(chartDays)
        : dashboardApi.getGraphDataForExpired(chartDays);
    request
      .then((response) => {
        if (cancelled) return;
        const data = response.data.graphResp.map((row) => ({
          ...row,
          date: formatDate(row.date),
          contractName: extractContract(row.contractName),
          count: Number(row.count),
        }));
        setChart({ data, yAxis: yAxisFor(Number(response.data.maxCount.totalCount)) });
      })
      .catch((error) => console.error('Error fetching chart data:', error))
      .finally(() => !cancelled && setChartLoading(false));
    return () => {
      cancelled = true;
    };
  }, [chartTab, chartDays]);

  const totals = useMemo(() => {
    if (!summary) return null;
    const sum = (field) => summary.byType.reduce((acc, t) => acc + t[field], 0);
    return {
      active: sum('active'),
      pending: sum('pending'),
      expiring: sum('expiring'),
    };
  }, [summary]);

  // the expiring list only holds the first page per type, so count from the summary
  const expiringTotal = totals ? totals.expiring : expiring.length;
  const attentionCount = pending.total + expiringTotal;

  const kpis = totals
    ? [
        {
          label: 'Active contracts',
          value: totals.active,
          hint: summary.byType.map((t) => `${t.type} ${t.active}`).join(' · '),
          tone: 'success',
        },
        {
          label: 'Waiting for approval',
          value: totals.pending,
          hint: totals.pending ? 'Review them in Approvals' : 'Nothing waiting',
          tone: totals.pending ? 'warning' : 'muted',
          onClick: canApprove ? () => navigate('/pendingApprovals') : undefined,
        },
        {
          label: `Expiring in ${period} days`,
          value: totals.expiring,
          hint: totals.expiring ? 'Plan renewals now' : 'No renewals due',
          tone: totals.expiring ? 'danger' : 'muted',
          onClick: () => document.getElementById('attention')?.scrollIntoView({ behavior: 'smooth' }),
        },
        {
          label: 'Active clients',
          value: Number(summary.clients.activeClient) || 0,
          hint: `${Number(summary.clients.clientWithoutNDA) || 0} without NDA · ${
            Number(summary.clients.clientWithoutMSA) || 0
          } without MSA`,
          tone: 'muted',
          onClick: () => navigate('/clients'),
        },
      ]
    : [];

  const renderAttentionRow = (row, kind) => {
    const isApproval = kind === 'approval';
    const detailPath = `/contract/${(row.type || '').toLowerCase()}/${row.contractId}`;
    const title = [row.type, row.projectName && row.projectName !== '-' ? row.projectName : null]
      .filter(Boolean)
      .join(' · ');
    return (
      <li key={`${kind}-${row.contractId}`} className="attn-row">
        <span className={`pill pill-${isApproval ? 'warning' : 'danger'}`}>
          {isApproval ? 'Approval' : 'Expiring'}
        </span>
        <div className="attn-main">
          <button type="button" className="link-strong" onClick={() => navigate(detailPath)}>
            {title || 'Contract'}
          </button>
          <span className="attn-meta">
            {row.legalEntityName}
            {isApproval && row.name && row.name !== '-' ? ` · ${row.name}` : ''}
            {!isApproval && row.expiryDate ? ` · ends ${row.expiryDate}` : ''}
          </span>
        </div>
        <span className="attn-due">
          {isApproval ? 'Waiting' : dueText(row.days)}
        </span>
        <Button onClick={() => navigate(detailPath)}>
          {isApproval ? 'Review' : 'Open'}
        </Button>
      </li>
    );
  };

  const attentionList = (rows, kind) =>
    attentionLoading ? (
      <div className="center-box"><Spin /></div>
    ) : rows.length === 0 ? (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={kind === 'approval' ? 'No approvals waiting' : `Nothing expires in the next ${period} days`}
        style={{ padding: '32px 0' }}
      />
    ) : (
      <ul className="attn-list">{rows.slice(0, 8).map((row) => renderAttentionRow(row, kind))}</ul>
    );

  const attentionTabs = [
    ...(canApprove
      ? [
          {
            key: 'approvals',
            label: <span>Approvals <span className="tab-count">{pending.total}</span></span>,
            children: (
              <>
                {attentionList(pending.rows, 'approval')}
                {pending.total > pending.rows.slice(0, 8).length && (
                  <div className="attn-more">
                    <Button type="link" onClick={() => navigate('/pendingApprovals')}>
                      See all {pending.total} approvals <RightOutlined />
                    </Button>
                  </div>
                )}
              </>
            ),
          },
        ]
      : []),
    {
      key: 'expiring',
      label: <span>Expiring <span className="tab-count">{expiringTotal}</span></span>,
      children: attentionList(expiring, 'expiring'),
    },
  ];

  const firstName = (employeeName || '').split(' ')[0];

  return (
    <div className="dash">
      <div className="dash-head">
        <div>
          <h1 className="dash-title">
            {greeting()}
            {firstName ? `, ${firstName}` : ''}
          </h1>
          <p className="dash-sub">
            {attentionLoading
              ? 'Checking what needs your attention…'
              : attentionCount === 0
              ? 'Nothing needs your attention right now.'
              : `${attentionCount} ${attentionCount === 1 ? 'item needs' : 'items need'} your attention.`}
          </p>
        </div>
        <Segmented options={PERIODS} value={period} onChange={setPeriod} />
      </div>

      {/* KPI cards */}
      <div className="kpi-grid">
        {summaryLoading && !summary
          ? [0, 1, 2, 3].map((i) => (
              <div key={i} className="card kpi"><Spin size="small" /></div>
            ))
          : kpis.map((kpi) =>
              kpi.label === 'Active contracts' ? (
                <div key={kpi.label} className="card kpi kpi-noclick">
                  <span className="kpi-label">{kpi.label}</span>
                  <span className="kpi-value">{kpi.value}</span>
                  <span className={`kpi-hint tone-${kpi.tone}`}>
                    {summary.byType.map((t, i) => (
                      <React.Fragment key={t.type}>
                        {i > 0 ? ' · ' : ''}
                        <button
                          type="button"
                          className="kpi-hint-link"
                          onClick={() => goToContracts(t.path, STATUS.active)}
                        >
                          {t.type} {t.active}
                        </button>
                      </React.Fragment>
                    ))}
                  </span>
                </div>
              ) : (
                <button
                  key={kpi.label}
                  type="button"
                  className="card kpi"
                  onClick={kpi.onClick}
                  disabled={!kpi.onClick}
                >
                  <span className="kpi-label">{kpi.label}</span>
                  <span className="kpi-value">{kpi.value}</span>
                  <span className={`kpi-hint tone-${kpi.tone}`}>{kpi.hint}</span>
                </button>
              )
            )}
      </div>

      <div className="dash-cols">
        {/* Needs attention */}
        <section id="attention" className="card attn" aria-labelledby="attn-title">
          <div className="card-head">
            <h2 id="attn-title" className="card-title">Needs attention</h2>
          </div>
          <Tabs items={attentionTabs} className="attn-tabs" />
        </section>

        <div className="dash-side">
          {/* Contracts by status */}
          <section className="card" aria-labelledby="status-title">
            <div className="card-head">
              <h2 id="status-title" className="card-title">Contracts by status</h2>
            </div>
            <div className="status-rows">
              {(summary?.byType || []).map((t) => {
                const total = t.active + t.pending + t.draft + t.inactive;
                return (
                  <div key={t.type} className="status-row">
                    <div className="status-row-head">
                      <button type="button" className="link-strong" onClick={() => navigate(`/${t.path}`)}>
                        {t.type}
                      </button>
                      <span className="muted">{total} total</span>
                    </div>
                    <div className="stack-bar">
                      {SEGMENTS.map((seg) =>
                        t[seg.key] > 0 ? (
                          <Tooltip key={seg.key} title={`${seg.label}: ${t[seg.key]}`}>
                            <button
                              type="button"
                              aria-label={`${t.type} ${seg.label}: ${t[seg.key]}`}
                              className="stack-seg"
                              style={{ width: `${(t[seg.key] / total) * 100}%`, background: seg.color }}
                              onClick={() => goToContracts(t.path, seg.status)}
                            />
                          </Tooltip>
                        ) : null
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="legend">
              {SEGMENTS.map((seg) => (
                <span key={seg.key}>
                  <i style={{ background: seg.color }} />
                  {seg.label}
                </span>
              ))}
            </div>
          </section>

          {/* Client coverage */}
          <section className="card" aria-labelledby="cov-title">
            <div className="card-head">
              <h2 id="cov-title" className="card-title">Client coverage</h2>
            </div>
            <div className="coverage">
              <button type="button" className="coverage-item" onClick={() => goToClients(0)}>
                <span className="coverage-value tone-danger">
                  {Number(summary?.clients?.clientWithoutNDA) || 0}
                </span>
                <span className="muted">clients without an NDA</span>
                <RightOutlined className="coverage-arrow" />
              </button>
              <button type="button" className="coverage-item" onClick={() => goToClients(1)}>
                <span className="coverage-value tone-danger">
                  {Number(summary?.clients?.clientWithoutMSA) || 0}
                </span>
                <span className="muted">clients without an MSA</span>
                <RightOutlined className="coverage-arrow" />
              </button>
            </div>
          </section>
        </div>
      </div>

      {/* Timeline chart */}
      <section className="card" aria-labelledby="chart-title">
        <div className="card-head wrap">
          <h2 id="chart-title" className="card-title">Contract timeline</h2>
          <div className="card-actions">
            <Segmented
              options={[
                { label: 'Expiring soon', value: 'expiring' },
                { label: 'Recently expired', value: 'expired' },
              ]}
              value={chartTab}
              onChange={setChartTab}
            />
            <Segmented
              options={[
                { label: '7d', value: 7 },
                { label: '15d', value: 15 },
                { label: '30d', value: 30 },
              ]}
              value={chartDays}
              onChange={setChartDays}
            />
          </div>
        </div>
        {chartLoading ? (
          <div className="center-box" style={{ height: 240 }}><Spin /></div>
        ) : chart.data.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No contracts in this window" style={{ padding: '48px 0' }} />
        ) : (
          <ChartComponent yaxisData={chart.yAxis} data={chart.data} />
        )}
      </section>
    </div>
  );
};

export default Dashboard;
